import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ExternalLink } from 'lucide-react';
import { ErrorLogEntry, ErrorLogFilters, ErrorTriageVerdict, useErrorLog, useResolveError } from '../../queries/errorLog';
import { useToast } from '../../contexts/ToastContext';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable';

const DATETIME = new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
const fmt = (v: string | null) => (v ? DATETIME.format(new Date(v)) : '—');
const SELECT =
  'h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const statusBadge = (s: ErrorLogEntry['status']) =>
  s === 'open' ? <Badge variant="destructive">Open</Badge> : s === 'fixed' ? <Badge variant="success">Fixed</Badge> : <Badge variant="outline">Ignored</Badge>;

const VERDICTS: Record<ErrorTriageVerdict, { label: string; variant: 'default' | 'primary' | 'warning' | 'destructive' | 'outline' }> = {
  code_bug: { label: 'Code bug', variant: 'destructive' },
  infra: { label: 'Infra', variant: 'warning' },
  data: { label: 'Data', variant: 'warning' },
  external: { label: 'External', variant: 'default' },
  needs_human: { label: 'Needs human', variant: 'primary' },
  noise: { label: 'Noise', variant: 'outline' },
};

const verdictBadge = (v: ErrorTriageVerdict | null) =>
  v ? <Badge variant={VERDICTS[v].variant}>{VERDICTS[v].label}</Badge> : <Badge variant="outline">Pending</Badge>;

const prLink = (url: string, label: string, className: string) => (
  <a href={url} target="_blank" rel="noopener noreferrer" className={className}>
    <ExternalLink className="h-3 w-3" aria-hidden="true" />
    {label}
  </a>
);

export function ErrorLogPage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('open');
  const [source, setSource] = useState('');
  const [verdict, setVerdict] = useState('');
  useEffect(() => setPage(1), [status, source, verdict]);
  const { data, isLoading } = useErrorLog(page, {
    status: status || undefined,
    source: source || undefined,
    verdict: (verdict || undefined) as ErrorLogFilters['verdict'],
  });
  const pagination = data?.pagination;

  const [selected, setSelected] = useState<ErrorLogEntry | null>(null);
  const [note, setNote] = useState('');
  const resolve = useResolveError();
  const { success, error } = useToast();

  const open = (e: ErrorLogEntry) => {
    setSelected(e);
    setNote(e.note ?? '');
  };

  const handleResolve = (next: 'fixed' | 'ignored') => {
    if (!selected) return;
    resolve.mutate(
      { id: selected.id, status: next, note: note || undefined },
      {
        onSuccess: () => {
          success(`Error marked ${next}.`);
          setSelected(null);
        },
        onError: () => error("Couldn't update the error."),
      },
    );
  };

  const columns: DataTableColumn<ErrorLogEntry>[] = [
    { key: 'last', header: 'Last seen', priority: true, render: (e) => <span className="text-muted-foreground @2xl:text-foreground">{fmt(e.last_seen_at)}</span> },
    { key: 'source', header: 'Source', priority: false, render: (e) => <Badge variant="default">{e.source}</Badge> },
    { key: 'context', header: 'Where', priority: false, render: (e) => <span className="text-muted-foreground">{e.context ?? '—'}</span> },
    { key: 'message', header: 'Message', priority: true, render: (e) => <span className="line-clamp-2 font-medium">{e.message}</span> },
    { key: 'count', header: 'Count', priority: false, alignEnd: true, render: (e) => <span className="tabular-nums">×{e.occurrences.toLocaleString()}</span> },
    {
      key: 'triage',
      header: 'Triage',
      priority: false,
      render: (e) => (
        <span className="inline-flex items-center gap-2">
          {verdictBadge(e.triage_verdict)}
          {e.triage_pr_url && prLink(e.triage_pr_url, 'PR', 'inline-flex items-center gap-1 text-xs text-primary underline')}
        </span>
      ),
    },
    { key: 'status', header: 'Status', priority: false, render: (e) => statusBadge(e.status) },
    {
      key: 'actions',
      header: '',
      priority: true,
      alignEnd: true,
      render: (e) => (
        <Button variant="outline" size="sm" onClick={() => open(e)}>
          Details
        </Button>
      ),
    },
  ];

  const executionUrl = typeof selected?.detail?.url === 'string' ? selected.detail.url : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Errors"
        description="Failures from n8n workflows and the graphql / admin-api containers. Repeats of an open error are grouped and counted; if a fixed error comes back it opens a new row."
      />

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status" className={SELECT}>
          <option value="open">Open</option>
          <option value="fixed">Fixed</option>
          <option value="ignored">Ignored</option>
          <option value="">All statuses</option>
        </select>
        <select value={source} onChange={(e) => setSource(e.target.value)} aria-label="Filter by source" className={SELECT}>
          <option value="">All sources</option>
          <option value="n8n">n8n</option>
          <option value="graphql">graphql</option>
          <option value="admin-api">admin-api</option>
        </select>
        <select value={verdict} onChange={(e) => setVerdict(e.target.value)} aria-label="Filter by triage" className={SELECT}>
          <option value="">All triage</option>
          <option value="untriaged">Pending</option>
          {(Object.keys(VERDICTS) as ErrorTriageVerdict[]).map((v) => (
            <option key={v} value={v}>{VERDICTS[v].label}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <DataTable columns={columns} data={data?.entries ?? []} getRowKey={(e) => String(e.id)} emptyMessage="No errors." />
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Page {pagination.page} of {pagination.pages} · {pagination.total.toLocaleString()} errors</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={pagination.page <= 1}>Previous</Button>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))} disabled={pagination.page >= pagination.pages}>Next</Button>
              </div>
            </div>
          )}
        </>
      )}

      <Dialog.Root open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[calc(100%-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-border bg-card p-6 shadow-xl">
            <Dialog.Title className="text-base font-semibold">{selected?.message}</Dialog.Title>
            <Dialog.Description className="mt-1 text-sm text-muted-foreground">
              {selected?.source} · {selected?.context ?? '—'} · ×{selected?.occurrences} · first {fmt(selected?.first_seen_at ?? null)}, last {fmt(selected?.last_seen_at ?? null)}
            </Dialog.Description>
            {executionUrl && (
              <a href={executionUrl} target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm text-primary underline">
                Open n8n execution
              </a>
            )}
            <section aria-label="Triage" className="mt-4 rounded-md border border-border p-3">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium">Triage</span>
                {verdictBadge(selected?.triage_verdict ?? null)}
                {selected?.triage_confidence && <span className="text-muted-foreground">{selected.triage_confidence} confidence</span>}
                {selected?.triaged_at && <span className="text-muted-foreground">· {fmt(selected.triaged_at)}</span>}
              </div>
              {selected?.triage_verdict ? (
                <>
                  {selected.triage_summary && <p className="mt-2 whitespace-pre-line text-sm">{selected.triage_summary}</p>}
                  {selected.triage_suggestion && (
                    <>
                      <h3 className="mt-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">Suggested fix</h3>
                      <p className="mt-1 whitespace-pre-line text-sm">{selected.triage_suggestion}</p>
                    </>
                  )}
                  {selected.triage_pr_url &&
                    prLink(selected.triage_pr_url, 'Open fix PR', 'mt-3 inline-flex items-center gap-1 text-sm text-primary underline')}
                </>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground">Not triaged yet — the homelab triage runs daily.</p>
              )}
            </section>
            <pre className="mt-4 max-h-80 overflow-auto rounded-md border border-border bg-muted p-3 font-mono text-xs whitespace-pre-wrap">
              {JSON.stringify(selected?.detail ?? {}, null, 2)}
            </pre>
            {selected?.status === 'open' ? (
              <>
                <label className="mt-4 block text-sm font-medium" htmlFor="error-note">Note (fix, PR link, reason ignored)</label>
                <input
                  id="error-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                <div className="mt-6 flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleResolve('ignored')} disabled={resolve.isPending}>Ignore</Button>
                  <Button size="sm" onClick={() => handleResolve('fixed')} disabled={resolve.isPending}>Mark fixed</Button>
                </div>
              </>
            ) : (
              <p className="mt-4 text-sm text-muted-foreground">
                {selected && statusBadge(selected.status)} {fmt(selected?.resolved_at ?? null)}{selected?.note ? ` — ${selected.note}` : ''}
              </p>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
